import { Button, HStack, Link, Spinner, Text, VStack } from "@chakra-ui/react"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Card from "@pathwright/ui/src/components/card/Card"
import useCardStackModal from "@pathwright/ui/src/components/card/useCardStackModal"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { CreateCohortCard } from "@pathwright/web/src/modules/cohort/create/CreateCohort"
import useCohortsQuery from "@pathwright/web/src/modules/cohort/switcher/useCohortsQuery"
import usePath from "@pathwright/web/src/modules/path/hooks/usePath"
import SyncCard from "@pathwright/web/src/modules/path/sync/SyncCard"
import { useCohortSyncPlanContext } from "@pathwright/web/src/modules/path/sync/SyncPlanContext"
import { getResourceSlugId } from "@pathwright/web/src/modules/utils/urls"
import { createCardRoutes } from "lib/core/routing/card-routing"
import LibraryResourceContainer from "library/show/components/LibraryResourceContainer"
import path from "path-browserify"
import { loadResourcePath } from "path/middleware"
import styled from "styled-components"

const StyledCard = styled(Card)`
  > .CardContainer {
    box-shadow: none;
  }

  > .CardContainer > .CardBody {
    min-height: 200px;
    ${(p) => p.transparent && "background-color: transparent;"}
  }

  > .CardContainer {
    overflow: hidden;
  }

  .Card__Nav {
    height: 0;
  }

  .Card__Nav .Card__back {
    // z-index: 1;
    display: none;
  }
`

const syncCardRoute = {
  getDefaultLaunchedFrom: ({ location }) => path.join(location.pathname, "../"),
  middleware: loadResourcePath,
  component: ({ location, match, card }) => {
    const resourceId = getResourceSlugId(match.params.resourceSlug)

    const { tc, Trans } = useTranslate()

    const { offering, resource } = window.App?.getStore("resource").getState()

    const { cohorts, loading } = useCohortsQuery({
      resourceId
    })

    // If no Path provided via props, we query it here
    const { refetch: refetchPath } = usePath({
      pathId: null,
      cohortId: parseInt(offering?.id),
      skip: !offering?.id
    })

    const { forward, refetchSyncPlanQueries } = useCohortSyncPlanContext({
      cohortId: parseInt(offering?.id)
    })

    // // NOTE: this doesn't "insert" the card on top of the SyncCard...
    // // maybe we could design a card.push method to push another card on top?
    const {
      cardStack: createCohortCardStack,
      onOpenCardStack: onCreateCohort
    } = useCardStackModal({
      component: CreateCohortCard,
      props: {
        resourceId,
        onCohortCreated: () => {
          refetchPath()
          refetchSyncPlanQueries()
        }
      }
    })

    const showBlankSlate =
      Boolean(
        offering?.is_curriculum &&
          (cohorts?.length || 0) < 1 &&
          !resource.license_offerings?.length
      ) || createCohortCardStack

    const isCollection = resource?.resource_type === 30

    const showCreateCohortWarning =
      showBlankSlate && !forward?.groupedChanges?.syncable?.length

    const syncDir = location.query.dir

    const fromCohortId = location.query.cohortId || match.params.cohortId

    const sourceIds =
      syncDir && location.query.sourceIds
        ? { [syncDir]: location.query.sourceIds.split(",").map(Number) }
        : null

    return (
      <StyledCard
        className={"SyncCard"}
        card={card}
        navless
        noaction
        size="maxContent"
        transparent={!showBlankSlate}
      >
        {loading ? (
          <Spinner />
        ) : showBlankSlate ? (
          <>
            <BlankSlate
              icons={null}
              heading={tc("Create a Cohort to publish to")}
            >
              <VStack align="flex-start">
                <Text mb={0}>
                  <Trans i18nKey="createCohortToPublish">
                    Cohorts enable various groups to take a Path together. The
                    Source Path stores the latest version and updates all
                    Cohorts automatically.{" "}
                    <Link href="https://help.pathwright.com/en/articles/5159775-sync-how-to-keep-your-content-in-sync-across-multiple-cohorts">
                      Learn more about Source Paths & Cohorts
                    </Link>
                    .
                  </Trans>
                </Text>
                <Trans i18nKey="createCohortToPublish2">
                  <Text mb={0} textAlign="left">
                    To publish, first create at least one Cohort.
                  </Text>
                </Trans>
                {showCreateCohortWarning ? (
                  <HStack
                    align="center"
                    w="full"
                    backgroundColor="whitesmoke"
                    mt={2}
                    gap={4}
                    p={5}
                  >
                    <Pathicon icon="caution-triangle" />
                    <Text m={0}>
                      {isCollection
                        ? tc(
                            "Add a least one Path to this Collection before creating a new Cohort."
                          )
                        : tc(
                            "Add a least one Step with Saved Blocks content before creating a new Cohort."
                          )}
                    </Text>
                  </HStack>
                ) : null}
                <HStack w="full" justify="center" mt={4}>
                  <Button
                    onClick={onCreateCohort}
                    isDisabled={showCreateCohortWarning}
                    colorScheme="brand"
                    cursor="pointer"
                    leftIcon={<Pathicon icon="group-plus" />}
                  >
                    {tc("Create a Cohort")}
                  </Button>
                </HStack>
              </VStack>
            </BlankSlate>
            {createCohortCardStack}
          </>
        ) : (
          <SyncCard
            cohortId={parseInt(fromCohortId)}
            syncDir={syncDir}
            onCreateCohort={onCreateCohort}
            onClose={card?.onClose}
            defaultSelectedItemSourceIDs={sourceIds}
          />
        )}
      </StyledCard>
    )
  }
}

const cardRoutes = {
  sync: syncCardRoute,
  stepSync: {
    ...syncCardRoute,
    path: ":pathItemType(step)/:pathItemSourceId/sync/"
  }
}

export default createCardRoutes(
  "/library/:resourceSlug/:cohortId/:section/",
  cardRoutes,
  LibraryResourceContainer
)
